// src/contexts/SiteConfigContext.tsx
//
// Fetches the server-driven runtime site config once on app load and exposes it
// to the tree. Two things live here, both changeable from the admin portal
// WITHOUT a deploy:
//   • checkoutEnabled — the checkout kill switch. FeatureToggleContext reads it
//     and force-disables PaymentProcess when it's false. Fail-open: until the
//     config loads (or if it fails), `checkoutEnabled` is undefined and the
//     FeatureToggle keeps its hardcoded default (ON) — a config blip can never
//     take checkout down.
//   • catalogEpoch — a monotonic counter. CatalogContext drops its localStorage
//     catalog caches when the server epoch exceeds the one it last stored, so an
//     admin "refresh cache for everyone" bump reaches every visitor on their
//     next load — or, for a tab that's already open, the next time it's
//     brought to the foreground (see the visibilitychange re-check below).
//   • classicItemIds — the homepage "Classics" group (FeaturedProductList).
//     Fail-open: an empty list (the default, and what a failed fetch leaves)
//     renders the original single ungrouped carousel.
//   • homepageExcludedItemIds — products in NEITHER carousel. Fail-open the
//     same way: empty means nothing is excluded, so a config blip shows a
//     product too many rather than hiding the homepage.
//
// Mounted ABOVE CatalogProvider and FeatureToggleProvider (see App.tsx) so both
// can consume it.

import React, { createContext, useContext, useEffect, useState } from "react";
import { getSiteConfig } from "../api/config";

interface SiteConfigContextValue {
  /** undefined until the config has loaded (or if the fetch failed). */
  checkoutEnabled: boolean | undefined;
  /** undefined until the config has loaded. */
  catalogEpoch: number | undefined;
  /** Homepage "Classics" item ids. Empty until loaded, and after a failure. */
  classicItemIds: string[];
  /** Item ids kept out of both homepage carousels. Empty until loaded. */
  homepageExcludedItemIds: string[];
  loaded: boolean;
}

const SiteConfigContext = createContext<SiteConfigContextValue | null>(null);

export const SiteConfigProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [checkoutEnabled, setCheckoutEnabled] = useState<boolean | undefined>(
    undefined,
  );
  const [catalogEpoch, setCatalogEpoch] = useState<number | undefined>(
    undefined,
  );
  const [classicItemIds, setClassicItemIds] = useState<string[]>([]);
  const [homepageExcludedItemIds, setHomepageExcludedItemIds] = useState<
    string[]
  >([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Refetched on mount AND whenever the tab is brought back to the
    // foreground, throttled to once a minute. Fetching only at mount meant a
    // tab that was already open when an admin bumped `catalogEpoch` never
    // learned about it: it kept serving (and re-writing) the pre-bump catalog
    // while freshly-loaded tabs showed the new one. Coming back to a tab is the
    // moment its staleness starts to matter, so that's when we re-check.
    let lastFetch = 0;
    const REFETCH_INTERVAL_MS = 60_000;

    const load = async () => {
      lastFetch = Date.now();
      const res = await getSiteConfig();
      if (cancelled) return;
      if (res && !res.error && res.data) {
        setCheckoutEnabled(res.data.checkoutEnabled);
        setCatalogEpoch(res.data.catalogEpoch);
        if (Array.isArray(res.data.classicItemIds)) {
          setClassicItemIds(res.data.classicItemIds);
        }
        if (Array.isArray(res.data.homepageExcludedItemIds)) {
          setHomepageExcludedItemIds(res.data.homepageExcludedItemIds);
        }
      }
      // Mark loaded even on failure so consumers stop waiting and fall back to
      // their hardcoded defaults (fail-open).
      setLoaded(true);
    };

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastFetch < REFETCH_INTERVAL_MS) return;
      load();
    };

    load();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  return (
    <SiteConfigContext.Provider
      value={{
        checkoutEnabled,
        catalogEpoch,
        classicItemIds,
        homepageExcludedItemIds,
        loaded,
      }}
    >
      {children}
    </SiteConfigContext.Provider>
  );
};

export const useSiteConfig = (): SiteConfigContextValue => {
  const context = useContext(SiteConfigContext);
  if (!context) {
    throw new Error("useSiteConfig must be used within a SiteConfigProvider");
  }
  return context;
};
